import React, { Fragment, useState, useEffect } from "react";
import {
  Col,
  Card,
  CardHeader,
  CardBody,
  Form,
  FormGroup,
  Label,
  Row,
} from "reactstrap";
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { H3, Breadcrumbs, Btn } from "../../../AbstractElements";
import { getDataWithToken, postDataWithToken } from "../../../Utils/restUtils";
import { addCompanyModule, getCompanyList, getIndustryData, getModuleList } from "../../../CommonURL";
import Typeahead from "../../../CommonElements/TypeAhead";
import Transparentspinner from "../../Common/loader/Transparentspinner";

const UserModuleAdd = () => {
  const {
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm();
  const [loading, setLoading] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [industryOptions, setIndustryOptions] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [moduleOptions, setModuleOptions] = useState([]);

  const navigate = useNavigate();
  const selectedIndustry = watch("industry_type");

  useEffect(() => {
    fetchInitialDropdowns();
  }, []);

  const fetchInitialDropdowns = async () => {
    try {
      // 1. Fetch Industries using getIndustryData
      const industryRes = await getDataWithToken(getIndustryData);
      const industryList =
        industryRes?.data?.data ||
        industryRes?.data ||
        (Array.isArray(industryRes) ? industryRes : []);
      if (Array.isArray(industryList) && industryList.length > 0) {
        setIndustryOptions(
          industryList.map((ind) => ({
            value: ind.id,
            label: ind.name || `Industry ${ind.id}`,
          }))
        );
      }

      // 2. Fetch Modules using getModuleList
      const moduleRes = await getDataWithToken(getModuleList);
      const modulesList =
        moduleRes?.data?.data ||
        moduleRes?.data ||
        (Array.isArray(moduleRes) ? moduleRes : []);
      if (Array.isArray(modulesList) && modulesList.length > 0) {
        setModuleOptions(
          modulesList.map((m) => ({
            value: m.id,
            label: m.name || `Module ${m.id}`,
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching dropdown options:", error);
    }
  };

  // Fetch companies based on selected Industry ID
  const fetchCompaniesByIndustry = async (industryId) => {
    if (!industryId) {
      setCompanyOptions([]);
      return;
    }
    setLoadingCompanies(true);
    try {
      let res = await getDataWithToken(
        `${getCompanyList}?industry_type_id=${industryId}`
      );
      let list = res?.data?.data || res?.data || (Array.isArray(res) ? res : []);

      if (!list || list.length === 0) {
        const postRes = await postDataWithToken(getCompanyList, {
          industry_id: industryId,
          industry_type_id: industryId,
        });
        list = postRes?.data?.data || postRes?.data || (Array.isArray(postRes) ? postRes : []);
      }

      if (Array.isArray(list)) {
        setCompanyOptions(
          list.map((item) => ({
            value: item.id || item.company_id,
            label:
              item.address && item.address !== "null" && item.address !== null
                ? `${item.name || item.company_name} - ${item.address}`
                : item.name || item.company_name || `Company ${item.id}`,
            company_name: item.name || item.company_name || "",
          }))
        );
      } else {
        setCompanyOptions([]);
      }
    } catch (error) {
      console.error("Error fetching companies for industry:", error);
      setCompanyOptions([]);
    } finally {
      setLoadingCompanies(false);
    }
  };

  const handleIndustryChange = (selectedOption) => {
    setValue("industry_type", selectedOption, { shouldValidate: true });
    setValue("company", null); // Reset selected company when industry changes
    setCompanyOptions([]);

    if (selectedOption?.value) {
      fetchCompaniesByIndustry(selectedOption.value);
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    const companyName =
      data.company?.company_name ||
      (data.company?.label?.includes(" - ")
        ? data.company.label.split(" - ")[0]
        : data.company?.label);

    const postRequest = {
      company_id: data.company?.value,
      company_name: companyName,
      module_id: data.module?.value,
      industry_type_id: data.industry_type?.value,
    };

    try {
      const response = await postDataWithToken(addCompanyModule, postRequest);
      if (
        response?.data?.status === true ||
        response?.status === 200 ||
        response?.data?.message === "success"
      ) {
        toast.success(response?.data?.message || "Company Module added successfully");
        navigate(`${process.env.PUBLIC_URL}/configuration/company-modules`);
      } else {
        toast.error(response?.data?.message || "Failed to add company module");
      }
    } catch (error) {
      console.error("Error adding company module:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(`${process.env.PUBLIC_URL}/configuration/company-modules`);
  };

  return (
    <Fragment>
      {(loading || loadingCompanies) && <Transparentspinner />}

      <Breadcrumbs
        mainTitle="Add Company Module"
        parent="Configuration"
        title="Add Company Module"
      />

      <Card>
        <CardHeader className="pb-0">
          <H3>Add Company Module</H3>
        </CardHeader>
        <CardBody>
          <Form
            className="needs-validation"
            noValidate
            onSubmit={handleSubmit(onSubmit)}
          >
            <Row className="g-3">
              {/* Industry Type Dropdown */}
              <Col md="6">
                <FormGroup>
                  <Label className="form-label" for="industry_type">
                    Industry Type <span className="text-danger">*</span>
                  </Label>
                  <Controller
                    name="industry_type"
                    control={control}
                    rules={{ required: "Please select an industry type" }}
                    render={({ field }) => (
                      <Typeahead
                        options={industryOptions}
                        value={field.value}
                        onChange={(selected) => handleIndustryChange(selected)}
                        placeholder="Select Industry Type"
                      />
                    )}
                  />
                  {errors.industry_type && (
                    <span className="text-danger small">
                      {errors.industry_type.message}
                    </span>
                  )}
                </FormGroup>
              </Col>

              {/* Company Dropdown (dependent on selected Industry) */}
              <Col md="6">
                <FormGroup>
                  <Label className="form-label" for="company">
                    Company <span className="text-danger">*</span>
                  </Label>
                  <Controller
                    name="company"
                    control={control}
                    rules={{ required: "Please select a company" }}
                    render={({ field }) => (
                      <Typeahead
                        options={companyOptions}
                        value={field.value}
                        onChange={field.onChange}
                        placeholder={
                          selectedIndustry?.value
                            ? loadingCompanies
                              ? "Loading companies..."
                              : companyOptions.length > 0
                              ? "Select Company"
                              : "No companies found for this industry"
                            : "Select Industry first"
                        }
                        disabled={!selectedIndustry?.value || loadingCompanies}
                      />
                    )}
                  />
                  {errors.company && (
                    <span className="text-danger small">
                      {errors.company.message}
                    </span>
                  )}
                </FormGroup>
              </Col>

              {/* Module Dropdown */}
              <Col md="6">
                <FormGroup>
                  <Label className="form-label" for="module">
                    Module <span className="text-danger">*</span>
                  </Label>
                  <Controller
                    name="module"
                    control={control}
                    rules={{ required: "Please select a module" }}
                    render={({ field }) => (
                      <Typeahead
                        options={moduleOptions}
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Select Module"
                      />
                    )}
                  />
                  {errors.module && (
                    <span className="text-danger small">
                      {errors.module.message}
                    </span>
                  )}
                </FormGroup>
              </Col>
            </Row>

            <div className="d-flex gap-2 mt-4">
              <Btn
                attrBtn={{
                  color: "primary",
                  type: "submit",
                  disabled: loading || loadingCompanies,
                }}
              >
                Submit Form
              </Btn>
              <Btn
                attrBtn={{
                  color: "secondary",
                  type: "button",
                  disabled: loading,
                  onClick: handleCancel,
                }}
              >
                Cancel
              </Btn>
            </div>
          </Form>
        </CardBody>
      </Card>
    </Fragment>
  );
};

export default UserModuleAdd;
